import React, { useMemo } from 'react';
import { Divider, Grid, IconButton, Stack, Typography } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';
import { ReactComponent as UploadCreative } from 'assets/images/cloud_upload.svg';
import { Website, BillingMethod, CampaignFields, CampaignStep } from 'types';
import { useCampaignForm, useIsMobile } from 'hooks';
import { DateTime } from 'luxon';
import { formatDate, getFormattedChfCurrency, getDayWord, isDiscountActive } from 'utils';
import { billingMethodTexts } from 'components/RightSidebar';
import { Card } from 'components/Card';
import { MAX_CREATIVES_AMOUNT } from 'shared';
import { DateRange } from '@mui/x-date-pickers-pro';
import { theme } from 'theme';

function getDurationValue(period: DateRange<DateTime>) {
  const [start, end] = period;

  if (!start || !end) return null;

  const diffInDays = end.diff(start, 'days').toObject().days;
  const ceilDays = diffInDays !== undefined && Number.isFinite(diffInDays) ? Math.ceil(diffInDays) : null;

  return {
    days: ceilDays ? `${ceilDays} ${getDayWord(ceilDays)}` : 'N/A',
    duration: `${formatDate(start)} - ${formatDate(end)}`,
  };
}

export const CampaignSummary = () => {
  const { getValues, redirectToStep } = useCampaignForm();
  const { isMobile } = useIsMobile();

  const formValues = getValues();
  const showDiscountActive = isDiscountActive();

  const campaignDuration = getDurationValue(formValues[CampaignFields.campaignPeriod]);

  const config = useMemo(
    () =>
      [
        {
          type: 'row',
          name: 'Name der Kampagne',
          value: formValues[CampaignFields.name],
          action: {
            onClick: () => redirectToStep(CampaignStep.Basic),
          },
        },
        {
          type: 'row',
          name: 'Dauer',
          value: campaignDuration ? (
            <>
              {campaignDuration.days} <br /> {campaignDuration?.duration}
            </>
          ) : (
            'N/A Tage'
          ),
          action: {
            onClick: () => redirectToStep(CampaignStep.Basic),
          },
        },
        {
          type: 'row',
          name: 'Gesamtbudget exkl MwSt',
          value: getFormattedChfCurrency(formValues[CampaignFields.budget]),
          action: {
            onClick: () => redirectToStep(CampaignStep.Basic),
          },
        },
        ...(showDiscountActive
          ? [
              {
                type: 'row',
                name: 'Rabatt',
                value: (
                  <Typography color="success.dark" component="span">
                    50% vom Gesamtbudget bei gleicher Leistung.
                  </Typography>
                ),
              },
            ]
          : []),
        { type: 'divider' },
        {
          type: 'row',
          name: 'Preismodell',
          value: billingMethodTexts[formValues[CampaignFields.billingMethod] as BillingMethod],
          action: {
            onClick: () => redirectToStep(CampaignStep.KPI),
          },
        },
        !!formValues[CampaignFields.maxUnitPrice] && {
          type: 'row',
          name: 'Max. Preis pro Einheit',
          value: getFormattedChfCurrency(formValues[CampaignFields.maxUnitPrice]),
          action: {
            onClick: () => redirectToStep(CampaignStep.KPI),
          },
        },
        // temp hidden, needs rework
        // {
        //   type: 'row',
        //   name: 'Berechnetes Ergebnis',
        //   value: '500 - 600 Engagements möglich',
        // },
        { type: 'divider' },

        ...formValues[CampaignFields.websites].map((website: Website) => ({
          type: 'row',
          name: website.url,
          value: !website.creatives.length ? (
            <Typography
              component="span"
              onClick={() => redirectToStep(CampaignStep.Creatives)}
              color={'rose.500'}
              sx={{ cursor: 'pointer' }}
            >
              Fehlende Creatives hochladen
            </Typography>
          ) : (
            `${website.creatives.length} von ${MAX_CREATIVES_AMOUNT} Creatives hochgeladen`
          ),
          action: {
            onClick: () => redirectToStep(CampaignStep.Creatives),
          },
          iconOverride: website.creatives.length ? null : <UploadCreative />,
        })),

        { type: 'divider' },

        {
          type: 'row',
          name: 'Zielgruppen',
          // TODO: change this test for new targeting
          value: formValues[CampaignFields.targetingGuidance]
            ? 'Automatisch optimiert und zusätzlicher Vorschlag'
            : 'Automatisch optimiert',
          action: {
            onClick: () => redirectToStep(CampaignStep.Targeting),
          },
        },

        ...(formValues[CampaignFields.billingMethod] !== BillingMethod.CLICK
          ? [
              { type: 'divider' },

              {
                type: 'row',
                name: 'GA4',
                value: 'Verbunden',
              },
            ]
          : []),
      ].filter(Boolean),
    [formValues, campaignDuration, redirectToStep, showDiscountActive]
  );

  return (
    <Card sx={{ pr: isMobile ? '0' : '30%' }}>
      <Stack gap={4}>
        {config.map(
          (
            {
              type,
              name,
              value,
              action,
              iconOverride,
            }: (typeof config)[number] & { iconOverride?: React.ReactElement | null },
            index
          ) => {
            if (type === 'divider') {
              return <Divider key={`divider-${index}`} />;
            }
            return (
              <Grid key={name} container>
                <Grid item xs={6} sx={{ alignContent: 'center', pr: isMobile ? theme.spacing(2) : 0 }}>
                  <Typography
                    variant="h4"
                    sx={{ wordBreak: 'break-word', ...(name === 'Rabatt' ? { color: 'success.dark' } : {}) }}
                  >
                    {name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    pr={isMobile ? theme.spacing(2) : 0}
                    justifyContent={isMobile ? 'space-between' : 'flex-start'}
                  >
                    <Typography>{value}</Typography>
                    {!!action?.onClick && (
                      <IconButton size="small" sx={{ p: 1, ml: 4 }} onClick={action?.onClick}>
                        {iconOverride || <EditOutlined fontSize="small" />}
                      </IconButton>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            );
          }
        )}
      </Stack>
    </Card>
  );
};
